'use client';

import dynamic from 'next/dynamic';
import { ComponentProps, FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { CAR_TRANSFER_FEE_NSW, PDP_ENQUIRY_FORM_ID, PLATE_TRANSFER_FEE } from 'src/constants';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { processPriceString } from 'src/general/helpers/processProductApiData';
import { formatFeaturesWithSorted, formatSpecs } from 'src/pdp/sections/Pdp/pdpHelpers';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';

const ExpandedGalleryViewFrameModal = dynamic(
  () =>
    import('../ExpandedGalleryViewFrame/ExpandedGalleryViewFrameModal').then(
      (mod) => mod.ExpandedGalleryViewFrameModal,
    ),
  { ssr: false },
);

const ModalGovernmentCharges = dynamic(
  () =>
    import('src/general/components/ModalGovernmentCharges/ModalGovernmentCharges').then(
      (mod) => mod.ModalGovernmentCharges,
    ),
  { ssr: false },
);

const ModalPurchaseInProgress = dynamic(
  () => import('../ModalPurchaseInProgress/ModalPurchaseInProgress').then((mod) => mod.ModalPurchaseInProgress),
  { ssr: false },
);

const CartCarSwitchModal = dynamic(
  () => import('../CartCarSwitchModal/CartCarSwitchModal').then((mod) => mod.CartCarSwitchModal),
  { ssr: false },
);

const FeatureModal = dynamic(() => import('../FeatureModal/FeatureModal').then((mod) => mod.FeatureModal), {
  ssr: false,
});

const CarEnquiryFormWrapper = dynamic(
  () => import('../CarEnquiryFormWrapper/CarEnquiryFormWrapper').then((mod) => mod.CarEnquiryFormWrapper),
  {
    ssr: false,
  },
);

type Props = {
  productDetails: ProductDetailsPayload;
  helperModals?: {
    imperfectionCriteria?: ReactNode;
  };
  microcopy: {
    governmentCharges: {
      header: ReactNode;
      description: ReactNode;
    };
    purchaseInProgress: ComponentProps<typeof ModalPurchaseInProgress>['microcopy'];
  };
};

export const PdpModals: FC<Props> = ({ productDetails, helperModals, microcopy }) => {
  const { currentModal, setCurrentModal } = pdpStore(
    ({ currentModal, setCurrentModal }) => ({
      currentModal,
      setCurrentModal,
    }),
    shallow,
  );

  const onClose = () => {
    setCurrentModal(null);
  };

  return (
    <>
      {currentModal?.name === 'gallery' && (
        <ExpandedGalleryViewFrameModal
          productDetails={productDetails}
          isOpen
          defaultViewType={currentModal.initialState}
          onClose={onClose}
          helperModals={helperModals}
        />
      )}
      <ModalGovernmentCharges
        isOpen={currentModal?.name === 'governmentCharges'}
        onClose={onClose}
        header={microcopy.governmentCharges.header}
        description={microcopy.governmentCharges.description}
        carPrice={processPriceString(productDetails.price)}
        dutyPrice={processPriceString(productDetails.dutyPrice)}
        transferFee={CAR_TRANSFER_FEE_NSW}
        plateTransferFee={PLATE_TRANSFER_FEE}
      />
      <ModalPurchaseInProgress
        productDetails={productDetails}
        isOpen={currentModal?.name === 'purchaseInProgress'}
        onClose={onClose}
        similarCarsUrl={`/product-detail/${productDetails.sku}/similar`}
        microcopy={microcopy.purchaseInProgress}
      />
      <CartCarSwitchModal
        isOpen={currentModal?.name === 'carSwitch'}
        onClose={onClose}
        car={productDetailPayloadToBaseCarData(productDetails)}
      />
      <FeatureModal
        isOpen={currentModal?.name === 'features'}
        onClose={onClose}
        {...formatFeaturesWithSorted(productDetails)}
        {...formatSpecs(productDetails)}
        initialTab={currentModal?.name === 'features' ? currentModal.initialTab : undefined}
      />
      <CarEnquiryFormWrapper
        isOpen={currentModal?.name === 'enquiry'}
        productDetailsPayload={productDetails}
        onClose={onClose}
        formId={PDP_ENQUIRY_FORM_ID}
      />
    </>
  );
};
